/* begin general import */
import {
  Add16,
  OverflowMenuHorizontal24,
  SettingsAdjust16,
  TrashCan16,
  Upload16,
  Websheet16,
  Download16,
} from "@carbon/icons-react";
import { Dropdown, Menu, PaginationProps, Spin } from "antd";
import { ColumnProps } from "antd/lib/table";
import PageHeader from "components/PageHeader/PageHeader";
import { STUDENT_ROUTE } from "config/route-consts";
import { Student, StudentFilter } from "models/Student";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionBarComponent,
  AdvanceIdFilterMaster,
  Button,
  OneLineText,
  Pagination,
  StandardTable,
  StatusLine,
  TagFilter,
} from "react3l-ui-library";
import { filterService } from "services/page-services/filter-service";
import { listService } from "services/page-services/list-service";
import { masterService } from "services/page-services/master-service";
import { queryStringService } from "services/page-services/query-string-service";
import {
  getAntOrderType,
  tableService,
} from "services/page-services/table-service";
import nameof from "ts-nameof.macro";
import StudentAdvanceFilter from "./StudentAdvanceFilter";
import { Status, StatusFilter } from "models/Status";
import { IdFilter } from "react3l-advanced-filters";
import { studentRepository } from "repositories/student-repository";
import { renderMasterIndex } from "helpers/table";
import { importExportService } from "services/page-services/import-export-service";
import { Gender, GenderFilter } from "models/Gender";
import { Major, MajorFilter } from "models/Major";
import InputSearch from "components/InputSearch";
import { Moment } from "moment";
import { formatDate } from "helpers/date-time";
import LayoutHeader from "components/LayoutHeader";
import LayoutCell from "components/LayoutCell";
import { formatNumber } from "helpers/number";
import TruncateCell from "components/TruncateCell";
import { Cohort, CohortFilter } from "models/Cohort";
import { EducationType, EducationTypeFilter } from "models/EducationType";
/* end individual import */

function StudentMaster() {
  const [translate] = useTranslation();
  const [modelFilter, dispatch] = queryStringService.useQueryString(
    StudentFilter,
    { skip: 0, take: 10 }
  );

  const {
    value: filter,
    handleChangeAllFilter,
    handleChangeSelectFilter,
    handleChangeInputSearch,
  } = filterService.useFilter(modelFilter, dispatch);

  const { list, count, loadingList, handleResetList, handleLoadList } =
    listService.useList(
      studentRepository.list,
      studentRepository.count,
      filter,
      dispatch
    );

  const { handleTableChange, handlePagination } = tableService.useTable(
    filter,
    handleChangeAllFilter
  );

  const {
    handleAction,
    handleBulkAction,
    canBulkAction,
    selectedRowKeys,
    rowSelection,
    setSelectedRowKeys,
  } = listService.useRowSelection(
    studentRepository.delete,
    studentRepository.bulkDelete,
    null,
    null,
    null,
    handleResetList
  );

  const { handleDeleteItem, handleGoDetail, handleGoCreate } =
    masterService.useMasterAction(STUDENT_ROUTE, handleAction);

  const { handleListExport, handleExportTemplateList } =
    importExportService.useExport();

  const {
    loading: loadingImport,
    handleImportList,
    ref: importButtonRef,
    handleClick,
  } = importExportService.useImport(handleResetList);

  const [visible, setVisible] = React.useState<boolean>(false);

  const menuAction = React.useCallback(
    (id: number, student: Student) => (
      <Menu>
        {
          <Menu.Item key="1">
            <div
              className="ant-action-menu text-center"
              onClick={handleGoDetail(id)}
            >
              {translate("general.actions.edit")}
            </div>
          </Menu.Item>
        }

        {!student?.used && (
          <Menu.Item key="2">
            <div
              className="ant-action-menu text-center"
              onClick={handleDeleteItem(student)}
            >
              {translate("general.actions.delete")}
            </div>
          </Menu.Item>
        )}
      </Menu>
    ),
    [handleGoDetail, translate, handleDeleteItem]
  );

  const pagination: PaginationProps = tableService.usePagination(filter, count);

  const columns: ColumnProps<Student>[] = useMemo(
    () => [
      {
        title: (
          <LayoutHeader
            orderType="left"
            title={translate("general.columns.index")}
            position="center"
          />
        ),
        key: "index",
        width: 60,
        render(...params: [number, Student, number]) {
          const index = renderMasterIndex<Student>(pagination)(...params);
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText value={formatNumber(index)} />
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "code"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("students.code")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].code),
        dataIndex: nameof(list[0].code),
        sorter: true,
        sortOrder: getAntOrderType<Student, StudentFilter>(
          filter,
          nameof(list[0].code)
        ),
        width: "10%",
        render(...params: [string, Student, number]) {
          return <TruncateCell renderContent={params[0]} />;
        },
      },

      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "name"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("students.name")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].name),
        dataIndex: nameof(list[0].name),
        sorter: true,
        sortOrder: getAntOrderType<Student, StudentFilter>(
          filter,
          nameof(list[0].name)
        ),
        render(...params: [string, Student, number]) {
          return <TruncateCell renderContent={params[0]} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "cohort"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("students.cohort")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].cohort),
        dataIndex: nameof(list[0].cohort),
        sorter: true,
        sortOrder: getAntOrderType<Student, StudentFilter>(
          filter,
          nameof(list[0].cohort)
        ),
        width: "10%",
        render(...params: [Cohort, Student, number]) {
          return <TruncateCell renderContent={params[0]?.name} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "className"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("students.className")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].className),
        dataIndex: nameof(list[0].className),
        sorter: true,
        sortOrder: getAntOrderType<Student, StudentFilter>(
          filter,
          nameof(list[0].className)
        ),
        width: "10%",
        render(...params: [string, Student, number]) {
          return <TruncateCell renderContent={params[0]} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "major"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("students.major")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].major),
        dataIndex: nameof(list[0].major),
        sorter: true,
        sortOrder: getAntOrderType<Student, StudentFilter>(
          filter,
          nameof(list[0].major)
        ),
        width: "15%",
        render(...params: [Major, Student, number]) {
          return <TruncateCell renderContent={params[0]?.name} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "educationType"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={"Hệ đào tạo"}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].educationType),
        dataIndex: nameof(list[0].educationType),
        sorter: true,
        sortOrder: getAntOrderType<Student, StudentFilter>(
          filter,
          nameof(list[0].educationType)
        ),
        width: "8%",
        render(...params: [EducationType, Student, number]) {
          return <TruncateCell renderContent={params[0]?.name} />;
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "doB"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("students.doB")}
              sortedColumn={sortedColumn}
              isSorter
              position="center"
            />
          );
        },
        key: nameof(list[0].doB),
        dataIndex: nameof(list[0].doB),
        sorter: true,
        sortOrder: getAntOrderType<Student, StudentFilter>(
          filter,
          nameof(list[0].doB)
        ),
        width: 120,
        render(...params: [Moment, Student, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md" position="center">
              <OneLineText value={params[0] ? formatDate(params[0]) : ""} />
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "gender"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("students.gender")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].gender),
        dataIndex: nameof(list[0].gender),
        sorter: true,
        sortOrder: getAntOrderType<Student, StudentFilter>(
          filter,
          nameof(list[0].gender)
        ),
        width: 100,
        render(...params: [Gender, Student, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]?.name} />
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "status"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("students.status")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].status),
        dataIndex: nameof(list[0].status),
        sorter: true,
        sortOrder: getAntOrderType<Student, StudentFilter>(
          filter,
          nameof(list[0].status)
        ),
        width: 150,
        render(...params: [Status, Student, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <StatusLine value={params[0]?.name} color={params[0]?.color} />
            </LayoutCell>
          );
        },
      },

      {
        title: () => {
          return (
            <LayoutHeader
              orderType="left"
              title={translate("general.actions.label")}
            />
          );
        },
        key: "action",
        dataIndex: nameof(list[0].id),
        fixed: "right",
        width: 80,
        align: "center",
        render(id: number, student: Student) {
          return (
            <div className="d-flex justify-content-center button-action-table">
              <Dropdown
                overlay={menuAction(id, student)}
                trigger={["click"]}
                placement="bottom"
                arrow
              >
                <OverflowMenuHorizontal24 />
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [translate, pagination, list, filter, menuAction]
  );

  const handleCancel = React.useCallback(() => {
    setVisible(false);
  }, []);

  return (
    <>
      <Spin spinning={loadingImport || loadingList}>
        <div className="page-content">
          <PageHeader
            title={translate("students.master.subHeader")}
            breadcrumbItems={[
              translate("students.master.header"),
              translate("students.master.subHeader"),
            ]}
          />

          <div className="page page-master m-l--sm m-r--xxl m-b--xxs">
            <div className="page-master__title p-l--sm p-t--xs p-b--xs">
              {translate("students.master.subHeader")}
            </div>
            <div className="page-master__content">
              <div className="page-master__tag-filter">
                <TagFilter
                  value={filter}
                  translate={translate}
                  keyTranslate={"students"}
                  handleChangeFilter={handleChangeAllFilter}
                  onClear={() => {
                    return 0;
                  }}
                />
              </div>
              {(!selectedRowKeys || selectedRowKeys?.length === 0) && (
                <div className="page-master__filter-wrapper d-flex align-items-center justify-content-between">
                  <div className="page-master__filter d-flex align-items-center justify-content-start">
                    <div className="d-flex align-items-center">
                      <div className="">
                        <AdvanceIdFilterMaster
                          value={filter[nameof(list[0].genderId)]["equal"]}
                          placeHolder={translate("students.placeholder.gender")}
                          classFilter={GenderFilter}
                          onChange={handleChangeSelectFilter({
                            fieldName: nameof(list[0].gender),
                            fieldType: "equal",
                            classFilter: IdFilter,
                          })}
                          getList={studentRepository.filterListGender}
                          title={translate("students.gender")}
                        />
                      </div>
                      <div className="">
                        <AdvanceIdFilterMaster
                          value={filter[nameof(list[0].majorId)]["equal"]}
                          placeHolder={translate("students.placeholder.major")}
                          classFilter={MajorFilter}
                          onChange={handleChangeSelectFilter({
                            fieldName: nameof(list[0].major),
                            fieldType: "equal",
                            classFilter: IdFilter,
                          })}
                          getList={studentRepository.filterListMajor}
                          title={translate("students.major")}
                        />
                      </div>
                      <div className="">
                        <AdvanceIdFilterMaster
                          value={filter[nameof(list[0].cohortId)]["equal"]}
                          placeHolder={translate("students.placeholder.cohort")}
                          classFilter={CohortFilter}
                          onChange={handleChangeSelectFilter({
                            fieldName: nameof(list[0].cohort),
                            fieldType: "equal",
                            classFilter: IdFilter,
                          })}
                          getList={studentRepository.filterListCohort}
                          title={translate("students.cohort")}
                        />
                      </div>
                      <div className="">
                        <AdvanceIdFilterMaster
                          value={filter[nameof(list[0].educationTypeId)]["equal"]}
                          placeHolder={"Chọn hệ đào tạo"}
                          classFilter={EducationTypeFilter}
                          onChange={handleChangeSelectFilter({
                            fieldName: nameof(list[0].educationType),
                            fieldType: "equal",
                            classFilter: IdFilter,
                          })}
                          getList={studentRepository.filterListEducationType}
                          title={"Hệ đào tạo"}
                        />
                      </div>
                      <div className="">
                        <AdvanceIdFilterMaster
                          value={filter[nameof(list[0].statusId)]["equal"]}
                          placeHolder={translate("students.placeholder.status")}
                          classFilter={StatusFilter}
                          onChange={handleChangeSelectFilter({
                            fieldName: nameof(list[0].status),
                            fieldType: "equal",
                            classFilter: IdFilter,
                          })}
                          getList={studentRepository.filterListStatus}
                          title={translate("students.status")}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="page-master__filter-action-search d-flex align-items-center">
                    <Button
                      type="icon-only-ghost"
                      icon={<SettingsAdjust16 />}
                      onClick={() => setVisible(true)}
                      className="btn--xl"
                    />
                    <InputSearch
                      value={filter?.search}
                      classFilter={StudentFilter}
                      placeHolder={translate("general.placeholder.search")}
                      onChange={handleChangeInputSearch}
                    />
                  </div>

                  <div className="page-master__actions  d-flex align-items-center justify-content-start">
                    <div className="page-master__filter-action d-flex align-items-center">
                      <input
                        ref={importButtonRef}
                        type="file"
                        style={{ display: "none" }}
                        id="master-import"
                        onClick={handleClick}
                        onChange={handleImportList(studentRepository.import)}
                      />
                      <Button
                        type="icon-only-ghost"
                        icon={<Download16 />}
                        onClick={handleListExport(
                          filter,
                          studentRepository.export
                        )}
                        className="btn--xl btn-grey ml-3"
                      />
                      <Button
                        type="icon-only-ghost"
                        icon={<Upload16 />}
                        onClick={() => {
                          importButtonRef.current.click();
                        }}
                        className="btn--xl btn-grey ml-3"
                      />
                      <Button
                        type="icon-only-ghost"
                        icon={<Websheet16 />}
                        onClick={handleExportTemplateList(
                          studentRepository.exportTemplate
                        )}
                        className="btn--xl btn-grey ml-3"
                      />
                      <Button
                        type="primary"
                        className="btn--lg"
                        icon={<Add16 />}
                        onClick={handleGoCreate}
                      >
                        {translate("general.actions.create")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="page-master__content-table">
              <ActionBarComponent
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
              >
                <Button
                  icon={<TrashCan16 />}
                  type="ghost-primary"
                  className="btn--lg"
                  disabled={!canBulkAction}
                  onClick={() => handleBulkAction(selectedRowKeys)}
                >
                  {translate("general.actions.delete")}
                </Button>
              </ActionBarComponent>
              <StandardTable
                rowKey={nameof(list[0].id)}
                columns={columns}
                dataSource={list}
                isDragable={true}
                tableSize={"md"}
                onChange={handleTableChange}
                rowSelection={rowSelection}
                spinning={loadingList}
                bordered
                scroll={{ y: 500 }}
              />

              <Pagination
                skip={filter?.skip}
                take={filter?.take}
                total={count}
                onChange={handlePagination}
              //canChangePageSize={false}
              />
            </div>
          </div>
        </div>
      </Spin>

      {visible && (
        <StudentAdvanceFilter
          visible={visible}
          handleClose={handleCancel}
          visibleFooter={true}
          loading={false}
          size={"sm"}
          filter={filter}
          setVisible={setVisible}
          handleChangeAllFilter={handleChangeAllFilter}
        />
      )}
    </>
  );
}
export default StudentMaster;
